/**
 * useSubscriptionPortalData — les LECTURES du portail Abonnement.
 *
 * Trois des douze appels du legacy vivent ici, dans le même ordre et avec les mêmes paramètres :
 *
 *   1. `getSubscribePricing`   — au montage et à chaque changement d'enfant (`:184-188`)
 *   2. `getParentPauseBalance` — dans le même effet, juste après (`:187`)
 *   7. `markActivationViewed`  — une fois par élève, dès que la carte de reprise est rendue en
 *                                vue parent (`:433-442`). C'est ce POST qui éteint les rappels
 *                                J-5 / J-3 ; fire-and-forget, comme les signaux d'intent.
 *  10. `getSubscribePricing`   — le bouton « Réessayer » de l'état d'erreur (`:1832-1835`)
 *
 * Et une relecture que le legacy n'a pas, parce qu'il n'avait pas « Modifier mes créneaux » :
 * `refreshSchedule`, après un changement de créneau.
 */
import { useCallback, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { liveService } from "../../../../../../data/services/liveService"
import { subscriptionService } from "../../../../../../data/services/subscriptionService"

export type SubscriptionPortalData = {
    /** « Réessayer » — le legacy ne relance que le tarif, pas la balance. */
    retry: () => void
    /** Recharge le tarif après une mutation (`:272`, `:290`). */
    refreshPricing: () => void
    /** Recharge la balance de pause après une annulation (`:271`). */
    refreshPauseBalance: () => void
    /**
     * Relit ce qu'un changement de créneau a bougé hors du portail. L'accueil reste monté sous son
     * onglet et ne relit rien au retour : sans elle, il montrait encore le cours quitté, sans sa
     * matière, et pas le rattrapage à planifier, jusqu'au prochain lancement de l'app.
     *  - le tarif (`formData.user`) : les cours des objectifs, d'où l'accueil tire la matière d'une séance ;
     *  - l'élève (`currentUser`) : la même lecture pour « Tous mes lives » ;
     *  - ses prochaines séances (`liveSessions`) : les séances, et les rattrapages à planifier.
     */
    refreshSchedule: () => void
}

export function useSubscriptionPortalData(args: {
    selectedUserId?: string | number | null
    /** `isParentMode && hasPlannedActivation` — la condition de la balise, calculée par le VM. */
    shouldMarkActivationViewed: boolean
    /** L'adresse de l'enfant affiché : ses séances se relisent en parent (`AsParent`) quand elle est connue. */
    studentEmail?: string | null
}): SubscriptionPortalData {
    const { selectedUserId, shouldMarkActivationViewed, studentEmail = null } = args
    const dispatch = useDispatch()

    const refreshPricing = useCallback(() => {
        dispatch(apiRequest(subscriptionService.getSubscribePricing) as never)
    }, [dispatch])

    const refreshPauseBalance = useCallback(() => {
        dispatch(apiRequest(subscriptionService.getParentPauseBalance) as never)
    }, [dispatch])

    const refreshSchedule = useCallback(() => {
        refreshPricing()
        dispatch(apiRequest(authService.getUser) as never)
        // Le même appel que l'accueil (`useParentHomeData.fetchLives`) : il remplit la tranche qu'il lit.
        if (studentEmail) {
            dispatch(
                apiRequest(
                    liveService.getAllMyNextLiveSessionsAsParent,
                    {},
                    {},
                    { student_email: studentEmail }
                ) as never
            )
        } else {
            dispatch(apiRequest(liveService.getAllMyNextLiveSessions, {}, {}, {}) as never)
        }
    }, [dispatch, refreshPricing, studentEmail])

    // Le legacy ne dépend que de `selectedUser?.userId` : une bascule d'enfant rejoue les deux
    // appels, un simple re-rendu ne les rejoue pas.
    useEffect(() => {
        refreshPricing()
        refreshPauseBalance()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUserId])

    // Le back ne retient que la PREMIÈRE balise (les suivantes sont des no-op) : le ref n'est là
    // que pour éviter un POST par re-rendu, et il se ré-arme quand on change d'enfant.
    const viewedSentForRef = useRef<string | number | null>(null)
    useEffect(() => {
        if (!shouldMarkActivationViewed) return
        const studentId = selectedUserId ?? null
        if (viewedSentForRef.current === studentId) return
        viewedSentForRef.current = studentId
        dispatch(apiRequest(subscriptionService.markActivationViewed) as never)
    }, [dispatch, selectedUserId, shouldMarkActivationViewed])

    return { retry: refreshPricing, refreshPricing, refreshPauseBalance, refreshSchedule }
}
